import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Button,
  useIsMobile,
} from '@design-system';
import { License, LicenseCustomMetadata } from '@/features/licenses/types';

const customLicenseSchema = z.object({
  displayName: z.string().optional(),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  organizationName: z.string().min(1, 'Legal organization name is required'),
  organizationDisplayName: z.string().optional(),
  email: z.string().email('Invalid email address'),
  businessPhoneNumber: z
    .string()
    .refine(val => !val || val.replace(/\D/g, '').length >= 10, {
      message: 'Invalid phone number format.',
    }),
  address: z.string().optional(),
});

type FormValues = z.infer<typeof customLicenseSchema>;

interface CustomLicenseModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (metadata: LicenseCustomMetadata, displayName?: string) => void;
  isLoading: boolean;
  editingLicense: License | null;
}

export function CustomLicenseModal({
  open,
  onClose,
  onSave,
  isLoading,
  editingLicense,
}: CustomLicenseModalProps) {
  const isMobile = useIsMobile();
  const form = useForm<FormValues>({
    resolver: zodResolver(customLicenseSchema),
    defaultValues: {
      displayName: '',
      firstName: '',
      lastName: '',
      organizationName: '',
      organizationDisplayName: '',
      email: '',
      businessPhoneNumber: '',
      address: '',
    },
  });

  useEffect(() => {
    if (open) {
      if (editingLicense) {
        const metadata = editingLicense.metadata as LicenseCustomMetadata;
        form.reset({
          displayName: editingLicense.displayName || '',
          firstName: metadata.firstName || '',
          lastName: metadata.lastName || '',
          organizationName: metadata.organizationName || '',
          organizationDisplayName: metadata.organizationDisplayName || '',
          email: metadata.email || '',
          businessPhoneNumber: metadata.businessPhoneNumber || '',
          address: metadata.address || '',
        });
      } else {
        form.reset({
          displayName: '',
          firstName: '',
          lastName: '',
          organizationName: '',
          organizationDisplayName: '',
          email: '',
          businessPhoneNumber: '',
          address: '',
        });
      }
    }
  }, [editingLicense, open, form]);

  const onSubmit = (values: FormValues) => {
    const { displayName, ...metadata } = values;
    onSave(metadata as LicenseCustomMetadata, displayName || undefined);
  };

  const title = editingLicense ? 'Edit Custom Presenter' : 'Add Custom Presenter';
  const description =
    'Enter the details of the person or organization that will appear on policy illustrations.';

  // Shared form fields content
  const formFields = (
    <div className="grid gap-6 py-4">
      <FormField
        control={form.control}
        name="displayName"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Label (optional)</FormLabel>
            <FormControl>
              <Input
                placeholder="e.g., My Business Name"
                {...field}
              />
            </FormControl>
            <FormDescription>Friendly name to help you identify this presenter</FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="firstName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                First Name <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  placeholder="John"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="lastName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Last Name <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  placeholder="Doe"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={form.control}
        name="organizationName"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              Organization Name (Legal) <span className="text-destructive">*</span>
            </FormLabel>
            <FormControl>
              <Input
                placeholder="Legal entity name"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="organizationDisplayName"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Organization Name (Whitelabel)</FormLabel>
            <FormControl>
              <Input
                placeholder="Display name for presentations"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="email"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              Email <span className="text-destructive">*</span>
            </FormLabel>
            <FormControl>
              <Input
                type="email"
                placeholder="john@example.com"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="businessPhoneNumber"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Business Phone</FormLabel>
              <FormControl>
                <Input
                  type="tel"
                  placeholder="(555) 123-4567"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="address"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Address</FormLabel>
              <FormControl>
                <Input
                  placeholder="City, State"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer
        open={open}
        onOpenChange={onClose}
      >
        <DrawerContent className="flex max-h-[90vh] flex-col">
          <DrawerHeader className="text-left">
            <DrawerTitle>{title}</DrawerTitle>
            <DrawerDescription>{description}</DrawerDescription>
          </DrawerHeader>
          <div className="flex-1 overflow-y-auto px-4">
            <Form {...form}>
              <form
                id="custom-license-form"
                onSubmit={form.handleSubmit(onSubmit)}
              >
                {formFields}
              </form>
            </Form>
          </div>
          <DrawerFooter className="pt-2">
            <Button
              type="submit"
              disabled={isLoading}
              form="custom-license-form"
            >
              {isLoading ? 'Saving...' : editingLicense ? 'Update Presenter' : 'Save Presenter'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onClose}
    >
      <DialogContent className="flex max-h-[90vh] flex-col overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="flex-1">
          <Form {...form}>
            <form
              id="custom-license-form"
              onSubmit={form.handleSubmit(onSubmit)}
            >
              {formFields}
            </form>
          </Form>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isLoading}
            form="custom-license-form"
          >
            {isLoading ? 'Saving...' : editingLicense ? 'Update Presenter' : 'Save Presenter'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
